import React, { forwardRef } from 'react';

interface Service {
  id: number;
  title: string;
  description: string;
  icon: string;
  color: string;
}

const Services: React.ForwardRefRenderFunction<HTMLElement> = (props, ref) => {
  const services: Service[] = [
    {
      id: 1,
      title: 'Web Application Development',
      description: 'Development of modern, responsive web applications using React.js and Next.js with clean, maintainable code that scales with your business.',
      icon: 'fa-code',
      color: 'blue'
    },
    {
      id: 2,
      title: 'Backend API Development',
      description: 'Creation of robust, secure and scalable RESTful APIs using ASP.NET Web API, with focus on performance, security, and proper architecture.',
      icon: 'fa-server',
      color: 'green'
    },
    {
      id: 3,
      title: 'Database Design & Development',
      description: 'Design and implementation of efficient database schemas, optimizing for performance and data integrity, using SQL Server, MySQL, or MongoDB.',
      icon: 'fa-database',
      color: 'purple'
    },
    {
      id: 4,
      title: 'Desktop Application Development',
      description: 'Building Windows desktop applications with modern UIs and seamless performance using .NET technologies.',
      icon: 'fa-desktop',
      color: 'red'
    },
    {
      id: 5,
      title: 'Full-Stack Integration',
      description: 'End-to-end development services connecting frontend and backend systems into a cohesive, efficient solution for your business needs.',
      icon: 'fa-layer-group',
      color: 'orange'
    },
    {
      id: 6,
      title: 'Code Review & Optimization',
      description: 'Analysis of existing codebases to improve performance, security, and maintainability while implementing best practices.',
      icon: 'fa-magnifying-glass-chart',
      color: 'teal'
    }
  ];

  // Function to get the appropriate background color based on the color prop
  const getBgColor = (color: string): string => {
    const colorMap: Record<string, string> = {
      blue: 'bg-blue-100',
      green: 'bg-green-100',
      purple: 'bg-purple-100',
      red: 'bg-red-100',
      orange: 'bg-orange-100',
      teal: 'bg-teal-100'
    };
    return colorMap[color] || 'bg-gray-100';
  };

  // Function to get the appropriate text color based on the color prop
  const getTextColor = (color: string): string => {
    const colorMap: Record<string, string> = {
      blue: 'text-blue-600',
      green: 'text-green-600',
      purple: 'text-purple-600',
      red: 'text-red-600',
      orange: 'text-orange-600',
      teal: 'text-teal-600'
    };
    return colorMap[color] || 'text-gray-600';
  };

  return (
    <section id="services" className="section-padding" ref={ref}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12 section-reveal" style={{ animationDelay: '0.2s' }}>
          <h2 className="text-white md:text-4xl font-bold section-title text-center">
            Services I Offer
          </h2>
          <p className="text-white mt-4 max-w-3xl mx-auto">
            I provide a range of development services to help businesses build powerful digital solutions.
            Each service is delivered with attention to detail and a focus on quality.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={service.id}
              className="service-card bg-white rounded-xl p-6 shadow-md border border-gray-100 hover:border-gray-200 section-reveal"
              style={{ animationDelay: `${0.4 + index * 0.2}s` }}
            >
              <div className={`service-icon ${getBgColor(service.color)} ${getTextColor(service.color)} w-16 h-16 rounded-lg flex items-center justify-center text-2xl mb-4`}>
                <i className={`fas ${service.icon}`}></i>
              </div>
              <h3 className="text-xl font-bold mb-3">{service.title}</h3>
              <p className="text-gray-600 mb-4">{service.description}</p>
              <a href="#contact" className={`${getTextColor(service.color)} inline-flex items-center font-medium`}>
                Request Service <i className="fas fa-arrow-right ml-2"></i>
              </a>
            </div>
          ))}
        </div>
        
        <div className="mt-16 bg-gray-50 rounded-2xl p-8 md:p-12 section-reveal" style={{ animationDelay: '1.6s' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <h3 className="text-2xl font-bold mb-4">My Development Process</h3>
              <div className="space-y-6">
                <div className="flex section-reveal" style={{ animationDelay: '1.8s' }}>
                  <div className="flex-shrink-0 mr-4">
                    <div className="bg-primary text-white w-8 h-8 rounded-full flex items-center justify-center">
                      1
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Discovery & Planning</h4>
                    <p className="text-gray-600">I begin by understanding your business goals, target audience, and project requirements to create a detailed development plan.</p>
                  </div>
                </div>
                <div className="flex section-reveal" style={{ animationDelay: '2.0s' }}>
                  <div className="flex-shrink-0 mr-4">
                    <div className="bg-primary text-white w-8 h-8 rounded-full flex items-center justify-center">
                      2
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Design & Architecture</h4>
                    <p className="text-gray-600">I design the user interface and system architecture with a focus on usability, performance, and scalability.</p>
                  </div>
                </div>
                <div className="flex section-reveal" style={{ animationDelay: '2.2s' }}>
                  <div className="flex-shrink-0 mr-4">
                    <div className="bg-primary text-white w-8 h-8 rounded-full flex items-center justify-center">
                      3
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Development & Testing</h4>
                    <p className="text-gray-600">I implement the solution using clean code practices and rigorous testing to ensure quality and reliability.</p>
                  </div>
                </div>
                <div className="flex section-reveal" style={{ animationDelay: '2.4s' }}>
                  <div className="flex-shrink-0 mr-4">
                    <div className="bg-primary text-white w-8 h-8 rounded-full flex items-center justify-center">
                      4
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Deployment & Support</h4>
                    <p className="text-gray-600">I deploy your application to production and provide ongoing support and maintenance as needed.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold mb-4">Why Choose Me?</h3>
              <div className="space-y-4">
                <div className="flex items-start section-reveal" style={{ animationDelay: '2.6s' }}>
                  <div className="text-primary mr-3">
                    <i className="fas fa-check-circle text-lg"></i>
                  </div>
                  <p className="text-gray-700"><strong>Technical Expertise:</strong> Deep knowledge of React.js, Next.js, and ASP.NET Web API for complete fullstack solutions.</p>
                </div>
                <div className="flex items-start section-reveal" style={{ animationDelay: '2.8s' }}>
                  <div className="text-primary mr-3">
                    <i className="fas fa-check-circle text-lg"></i>
                  </div>
                  <p className="text-gray-700"><strong>Quality Focus:</strong> Emphasis on clean code, thorough testing, and maintainable solutions that stand the test of time.</p>
                </div>
                <div className="flex items-start section-reveal" style={{ animationDelay: '3.0s' }}>
                  <div className="text-primary mr-3">
                    <i className="fas fa-check-circle text-lg"></i>
                  </div>
                  <p className="text-gray-700"><strong>Collaborative Approach:</strong> I work closely with clients, providing regular updates and incorporating feedback throughout the development process.</p>
                </div>
                <div className="flex items-start section-reveal" style={{ animationDelay: '3.2s' }}>
                  <div className="text-primary mr-3">
                    <i className="fas fa-check-circle text-lg"></i>
                  </div>
                  <p className="text-gray-700"><strong>Problem Solver:</strong> I don't just write code; I solve business problems with thoughtful, effective technical solutions.</p>
                </div>
                <div className="flex items-start section-reveal" style={{ animationDelay: '3.4s' }}>
                  <div className="text-primary mr-3">
                    <i className="fas fa-check-circle text-lg"></i>
                  </div>
                  <p className="text-gray-700"><strong>On-time Delivery:</strong> Consistent record of delivering projects on schedule and within scope.</p>
                </div>
              </div>
              
              <div className="mt-8 section-reveal" style={{ animationDelay: '3.6s' }}>
                <a
                  href="#contact"
                  className="inline-block bg-primary hover:bg-blue-600 text-white py-3 px-8 rounded-full transition duration-300 font-semibold"
                >
                  Discuss Your Project
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default forwardRef(Services);